import { MonitorSettingsBase, MonitorSettings, MonitorSettingsEntry } from './MonitorSettings';
import { IpAddress } from '../Core/IpAddress';
import { StringUtils } from '../Utils/StringUtils';
import { ValueEncoder } from "../Utils/ValueEncoder";
import { MonitorType } from "../Core/MonitorType";


export class MonitorSettings_IpEntry extends MonitorSettingsEntry {

	public IpAddress: IpAddress;
	public get HasIpAddress(): boolean {
		return !!this.IpAddress;
	}

	public Hostname: string;
	public get HasHostname(): boolean {
		return !!this.Hostname;
	}

	public PollByHostname: boolean;

	public IfIndex: number;
	public get HasIfIndex(): boolean {
		return (this.IfIndex >= 0);
	}

	constructor(ipAddress?: IpAddress) {
		super();

		this._Init();
		if (ipAddress) {
			this.IpAddress = ipAddress.Clone();
		}
		else {
			this.IpAddress = null;
		}
	}

	private _Init(): void {
		this.IpAddress = null;
		this.Hostname = "";
		this.PollByHostname = false;
		this.IfIndex = -1;
	}

	public CopyConfig(other: MonitorSettings_IpEntry): void {
		if (!other)
			return;
		if (other == this)
			return;

		super.CopyConfig(other);

		this.IpAddress = null;
		if (other.HasIpAddress)
			this.IpAddress = other.IpAddress.Clone();

		this.Hostname = "";
		if (other.HasHostname)
			this.Hostname = other.Hostname;

		this.PollByHostname = other.PollByHostname;
		this.IfIndex = other.IfIndex;
	}

	public get DisplayName(): string {
		let result = "";

		if (this.HasIpAddress)
			result = StringUtils.AppendNameValuePair(result, "IP", this.IpAddress.ToString());
		if (this.HasHostname)
			result = StringUtils.AppendNameValuePair(result, "HOSTNAME", this.Hostname);
		if (this.HasIfIndex)
			result = StringUtils.AppendNameValuePair(result, "IF", this.IfIndex.toString());

		return result;
	}

	public Write(obj: any, context: any): void {
		obj.monitored_ips_entry = {};
		obj = obj.monitored_ips_entry;

		super.Write(obj, context);

		if (this.HasIpAddress)
			obj.ip_address = ValueEncoder.EncodeIpAddress(this.IpAddress);

		if (this.HasHostname)
			obj.host_name = this.Hostname;

		obj.poll_by_hostname = ValueEncoder.EncodeBoolean(this.PollByHostname);

		if (this.HasIfIndex)
			obj.if_index = ValueEncoder.EncodeInteger(this.IfIndex);
	}

	public Read(obj: any, context: any): void {
		if (!obj.monitored_ips_entry)
			throw new Error();
		obj = obj.monitored_ips_entry;

		super.Read(obj, context);

		if (obj.ip_address !== undefined)
			this.IpAddress = ValueEncoder.DecodeIpAddress(obj.ip_address);

		if (obj.host_name !== undefined)
			this.Hostname = obj.host_name;

		if (obj.poll_by_hostname !== undefined)
			this.PollByHostname = ValueEncoder.DecodeBoolean(obj.poll_by_hostname);

		if (obj.if_index !== undefined)
			this.IfIndex = ValueEncoder.DecodeInteger(obj.if_index);
	}
}


export enum PingResponseCheck {
	PingOnly = 0,
	PingPlusSnmp,
	SnmpOnly
}


export class MonitorSettings_Ping extends MonitorSettings<MonitorSettings_IpEntry> {

	private static readonly PING_DEFAULT_TIMEOUT = 500;
	private static readonly PING_DEFAULT_RETRIES = 0;
	private static readonly PING_DEFAULT_LIMIT_COUNT = 5;

	private static readonly PING_DEFAULT_CRITICAL_THRESHOLD = 5000;
	private static readonly PING_DEFAULT_WARNING_THRESHOLD = 3500;

	// METHODs to Checks
	public ResponseChecks: PingResponseCheck;
	public CheckUpTime: boolean;

	// PING/ICMP timeout/retries override
	public Timeout: number;
	public Retries: number;

	constructor() {
		super();

		this.MonitorType = MonitorType.Ping;
		this.ResponseChecks = PingResponseCheck.PingOnly;
		this.CheckUpTime = false;
		this.Timeout = MonitorSettings_Ping.PING_DEFAULT_TIMEOUT;
		this.Retries = MonitorSettings_Ping.PING_DEFAULT_RETRIES;
		this.CriticalThreshold = MonitorSettings_Ping.PING_DEFAULT_CRITICAL_THRESHOLD;
		this.WarningThreshold = MonitorSettings_Ping.PING_DEFAULT_WARNING_THRESHOLD;
	}

	protected get ObjSerEntriesName(): string {
		return "monitored_ips";
	}

	protected CreateEntry(): MonitorSettings_IpEntry {
		return new MonitorSettings_IpEntry();
	}

	public Add_Ip(ipAddress: IpAddress): MonitorSettings_IpEntry {
		if (!ipAddress)
			return null;

		let pingEntry: MonitorSettings_IpEntry = this.Find_Ip(ipAddress);
		if (!pingEntry) {
			pingEntry = new MonitorSettings_IpEntry(ipAddress);
			this.Add(pingEntry);
		}

		return pingEntry;
	}

	public Add_IpHostPollBy(ipAddress: IpAddress, hostname: string, pollByHostname: boolean = false)
		: MonitorSettings_IpEntry {

		let pingIpEntry: MonitorSettings_IpEntry = null;

		pingIpEntry = this.Find_IpHost(ipAddress, hostname);

		if (!pingIpEntry) {
			pingIpEntry = new MonitorSettings_IpEntry();
			pingIpEntry.IpAddress = ipAddress;
			if (!!hostname)
				pingIpEntry.Hostname = hostname;

			pingIpEntry.PollByHostname = pollByHostname;

			this.Add(pingIpEntry);
		}

		return pingIpEntry;
	}

	public CopySettings(other: MonitorSettingsBase): void {
		if (!other)
			return;

		if (other == this)
			return;

		let otherSettings = other as MonitorSettings_Ping;
		if (!otherSettings)
			return;

		super.CopySettings(other);

		this.ResponseChecks = otherSettings.ResponseChecks;
		this.CheckUpTime = otherSettings.CheckUpTime;

		this.Timeout = otherSettings.Timeout;
		this.Retries = otherSettings.Retries;
	}

	public Contains_IpHost(ipAddress: IpAddress, hostname: string): boolean {
		return !!this.Find_IpHost(ipAddress, hostname);
	}

	public Find_IpHost(ipAddress: IpAddress, hostname: string): MonitorSettings_IpEntry {
		let ipEntry: MonitorSettings_IpEntry = null;

		if (!!hostname)
			ipEntry = this.Find_Host(hostname);

		if (ipEntry == null)
			ipEntry = this.Find_Ip(ipAddress);

		return ipEntry;
	}

	public Find_Ip(ipAddress: IpAddress): MonitorSettings_IpEntry {
		let pingEntry: MonitorSettings_IpEntry = null;

		if (!!ipAddress) {
			for (let i = 0; i < this.Count; ++i) {

				let entry: MonitorSettings_IpEntry = this.Get(i);

				if (!entry.HasIpAddress)
					continue;

				if (entry.IpAddress.CompareTo(ipAddress) != 0)
					continue;

				pingEntry = entry;
				break;
			}
		}

		return pingEntry;
	}

	public Find_Host(hostname: string): MonitorSettings_IpEntry {
		let pingEntry: MonitorSettings_IpEntry = null;

		if (!!hostname) {
			for (let i = 0; i < this.Count; ++i) {

				let entry: MonitorSettings_IpEntry = this.Get(i);

				if (!entry.HasHostname)
					continue;

				if (!StringUtils.CaseInsensitiveEquals(entry.Hostname, hostname))
					continue;

				pingEntry = entry;
				break;
			}
		}

		return pingEntry;
	}

	public Find(otherIpEntry: MonitorSettings_IpEntry): MonitorSettings_IpEntry {
		if (!otherIpEntry)
			return null;

		let ipEntry: MonitorSettings_IpEntry = null;

		if (otherIpEntry.HasGuid)
			ipEntry = this.Find_Guid(otherIpEntry.Guid);

		if (!ipEntry && otherIpEntry.HasHostname)
			ipEntry = this.Find_Host(otherIpEntry.Hostname);

		if (!ipEntry && otherIpEntry.HasIpAddress) {
			ipEntry = this.Find_Ip(otherIpEntry.IpAddress);
		}

		return ipEntry;
	}

	public Write(obj: any, context: any): void {
		obj.monitor_settings = {};
		obj = obj.monitor_settings;

		super.Write(obj, context);

		obj.response_checks = PingResponseCheck[this.ResponseChecks];

		obj.check_up_time = ValueEncoder.EncodeBoolean(this.CheckUpTime);

		obj.timeout = ValueEncoder.EncodeInteger(this.Timeout);

		obj.retries = ValueEncoder.EncodeInteger(this.Retries);
	}

	public Read(obj: any, context: any): void {
		if (!obj.monitor_settings)
			throw new Error();
		obj = obj.monitor_settings;

		super.Read(obj, context);

		if (obj.response_checks !== undefined)
			this.ResponseChecks = PingResponseCheck[<string>obj.response_checks];

		if (obj.check_up_time !== undefined)
			this.CheckUpTime = ValueEncoder.DecodeBoolean(obj.check_up_time);

		if (obj.timeout !== undefined)
			this.Timeout = ValueEncoder.DecodeInteger(obj.timeout);

		if (obj.retries !== undefined)
			this.Retries = ValueEncoder.DecodeInteger(obj.retries);
	}
}
